import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const PaymentTypeContext = createContext();
export const usePaymentTypes = () => useContext(PaymentTypeContext);

export const PaymentTypeProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();

  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentTypesLoading, setLoading] = useState(false);

  const mountedRef = useRef(true);

  const PAYMENTTYPE_ENDPOINT = "/payment-types";
  const baseURL = BASE_URL.endsWith("/") ? BASE_URL.slice(0, -1) : BASE_URL;

  /* -------------------- Mount Safety -------------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetPaymentTypes = (updater) => {
    if (!mountedRef.current) return;
    setPaymentTypes(updater);
  };

  /* -------------------- FETCH ALL -------------------- */
  const fetchPaymentTypes = useCallback(async () => {
    setLoading(true);
    try {
      if (!token) throw new Error("Missing auth token");

      const res = await fetch(`${baseURL}${PAYMENTTYPE_ENDPOINT}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.message || "Fetch failed");

      safeSetPaymentTypes(payload?.data || []);
      return payload;
    } catch (err) {
      console.error("[PaymentTypes] fetch error:", err);
      if (err.message?.includes("Token")) logout();
      safeSetPaymentTypes([]);
      throw err;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token, baseURL, logout]);

  /* -------------------- CREATE -------------------- */
  const addPaymentType = useCallback(
    async (data) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(`${baseURL}${PAYMENTTYPE_ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            ...data,
            create_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Create failed");

        await fetchPaymentTypes();
        return payload;
      } catch (err) {
        console.error("[PaymentTypes] add error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId, fetchPaymentTypes]
  );

  /* -------------------- UPDATE -------------------- */
  const updatePaymentType = useCallback(
    async (id, data) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(
          `${baseURL}${PAYMENTTYPE_ENDPOINT}/${id}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              ...data,
              update_audit_id: adminId,
            }),
          }
        );

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Update failed");

        await fetchPaymentTypes();
        return payload;
      } catch (err) {
        console.error("[PaymentTypes] update error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId, fetchPaymentTypes]
  );

  /* -------------------- DELETE -------------------- */
  const deletePaymentType = useCallback(
    async (id) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(
          `${baseURL}${PAYMENTTYPE_ENDPOINT}/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              delete_audit_id: adminId,
            }),
          }
        );

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Delete failed");

        safeSetPaymentTypes((prev) =>
          prev.filter((p) => String(p.id) !== String(id))
        );

        return payload;
      } catch (err) {
        console.error("[PaymentTypes] delete error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId]
  );

  /* -------------------- TOGGLE STATUS -------------------- */
  const toggleStatus = useCallback(
    async (id, currentStatus) => {
      const newStatus =
        currentStatus === "Active" ? "Inactive" : "Active";

      try {
        if (!token) throw new Error("Missing auth token");

        // Optimistic UI
        safeSetPaymentTypes((prev) =>
          prev.map((p) =>
            String(p.id) === String(id)
              ? { ...p, status: newStatus }
              : p
          )
        );

        const res = await fetch(
          `${baseURL}${PAYMENTTYPE_ENDPOINT}/${id}/status`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              status: newStatus,
              update_audit_id: adminId,
            }),
          }
        );

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Status update failed");

        return payload;
      } catch (err) {
        console.error("[PaymentTypes] toggleStatus error:", err);

        // rollback
        safeSetPaymentTypes((prev) =>
          prev.map((p) =>
            String(p.id) === String(id)
              ? { ...p, status: currentStatus }
              : p
          )
        );
        throw err;
      }
    },
    [token, baseURL, adminId]
  );

  /* -------------------- INITIAL LOAD -------------------- */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchPaymentTypes().catch(() => {});
  }, [isReady, token, fetchPaymentTypes]);

  return (
    <PaymentTypeContext.Provider
      value={{
        paymentTypes,
        paymentTypesLoading,
        fetchPaymentTypes,
        addPaymentType,
        updatePaymentType,
        deletePaymentType,
        toggleStatus,
      }}
    >
      {children}
    </PaymentTypeContext.Provider>
  );
};